package com.evoapps.evoshift.domain.forecast

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlin.math.ceil
import kotlin.math.max

/*
 * SH-120: from a day's expected net sales to the labour it carries, at a sales-per-labour-hour goal
 * (the spreadsheet's SPMH): hours = sales ÷ goal, rounded to half hours. The workplace's own goal
 * when set; else what it actually did over the last [HeadcountRules.HISTORY_WEEKS] weeks (takings ÷
 * hours worked). Without intraday sales the hours spread over the availability slots by their
 * length, so each slot gets the same number of people (at least one); the default all-day slot
 * says nothing about the day's shape and gets no split.
 */

/** Where the sales-per-labour-hour figure comes from. */
enum class SalesPerHourSource {
    /** The workplace's goal (settings). */
    GOAL,
    /** Takings ÷ hours worked over the last weeks. */
    HISTORY
}

/** A slot of the day, in minutes from midnight (end may be 24:00 = 1440). */
data class SlotWindow(val id: String, val label: String, val startMinute: Int, val endMinute: Int) {
    val minutes: Int get() = endMinute - startMinute
}

data class SlotHeadcount(val slotId: String, val label: String, val people: Int)

/** "≈ 17 h at 250 zł per labour hour: 2 people per slot". */
data class HeadcountSuggestion(
    val labourHours: Double,
    val salesPerHour: Double,
    val source: SalesPerHourSource,
    /** Empty when the workplace has no slots that shape the day. */
    val slots: List<SlotHeadcount>
)

object HeadcountRules {
    const val HISTORY_WEEKS = 8
    /** Days with both takings and hours needed for a historical figure. */
    const val MIN_DAYS_WITH_HOURS = 10

    /**
     * The workplace's takings per hour worked over the last [HISTORY_WEEKS] weeks before [before],
     * rounded to a whole unit;
     * null with fewer than [MIN_DAYS_WITH_HOURS] days that have both.
     */
    fun historicalSalesPerHour(history: List<SalesHistoryDay>, before: LocalDate): Double? {
        val from = before.minus(DatePeriod(days = 7 * HISTORY_WEEKS))
        val days = history.filter { it.date >= from && it.date < before && it.netSales > 0 && it.labourHours > 0 }
        if (days.size < MIN_DAYS_WITH_HOURS) return null
        // Whole currency units: the figure is shown, and a manager redoes the hours with it.
        return kotlin.math.round(days.sumOf { it.netSales } / days.sumOf { it.labourHours })
    }

    /** The goal when set (> 0), else the history's figure; null when neither is known. */
    fun salesPerHour(goal: Double?, history: List<SalesHistoryDay>, before: LocalDate): Pair<Double, SalesPerHourSource>? =
        goal?.takeIf { it > 0 }?.let { it to SalesPerHourSource.GOAL }
            ?: historicalSalesPerHour(history, before)?.let { it to SalesPerHourSource.HISTORY }

    fun suggest(netSales: Double, salesPerHour: Double, source: SalesPerHourSource, slots: List<SlotWindow>): HeadcountSuggestion {
        require(salesPerHour > 0) { "Sales per labour hour must be above zero" }
        val hours = roundToHalf(netSales / salesPerHour)
        val shaping = slots.filter { it.minutes in 1 until 24 * 60 }
        val total = shaping.sumOf { it.minutes } / 60.0
        val people = if (total > 0) max(1, ceil(hours / total - 1e-9).toInt()) else 0
        return HeadcountSuggestion(
            labourHours = hours,
            salesPerHour = salesPerHour,
            source = source,
            slots = shaping.map { SlotHeadcount(it.id, it.label, people) }
        )
    }

    private fun roundToHalf(hours: Double): Double = kotlin.math.round(hours * 2) / 2
}
